import React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

/**
 * Professional Theme Toggle Component
 * Provides smooth theme switching with visual feedback
 */
export function ThemeToggle({ showLabel = false, variant = 'button' }) {
  const { theme, isDark, toggleTheme, setLightTheme, setDarkTheme, resetToSystem } = useTheme();

  if (variant === 'dropdown') {
    return (
      <div className="theme-toggle-dropdown relative group">
        <button
          className="theme-toggle-button flex items-center justify-center rounded-lg transition-all duration-200 focus-ring"
          style={{
            width: '40px',
            height: '40px',
            backgroundColor: 'var(--bg-hover)',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border-primary)'
          }}
          title={`Current theme: ${theme}`}
        >
          {isDark ? (
            <Moon size={18} style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }} />
          ) : (
            <Sun size={18} style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }} />
          )}
        </button>

        {/* Dropdown Menu */}
        <div 
          className="theme-dropdown absolute right-0 mt-2 py-2 w-48 rounded-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
          style={{
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-primary)',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)'
          }}
        >
          <button
            onClick={setLightTheme}
            className="theme-option w-full flex items-center px-4 py-2 text-sm transition-colors duration-150"
            style={{
              color: theme === 'light' ? 'var(--text-primary)' : 'var(--text-secondary)',
              backgroundColor: theme === 'light' ? 'var(--bg-selected)' : 'transparent'
            }}
          >
            <Sun size={16} className="mr-3" />
            Light Theme
          </button>

          <button
            onClick={setDarkTheme}
            className="theme-option w-full flex items-center px-4 py-2 text-sm transition-colors duration-150"
            style={{
              color: theme === 'dark' ? 'var(--text-primary)' : 'var(--text-secondary)',
              backgroundColor: theme === 'dark' ? 'var(--bg-selected)' : 'transparent'
            }}
          >
            <Moon size={16} className="mr-3" />
            Dark Theme
          </button>
          
          <button
            onClick={resetToSystem}
            className="theme-option w-full flex items-center px-4 py-2 text-sm transition-colors duration-150"
            style={{
              color: 'var(--text-secondary)'
            }}
          >
            <Monitor size={16} className="mr-3" />
            System Default
          </button>
        </div>
      </div>
    );
  }

  // Simple toggle button
  return (
    <button
      onClick={toggleTheme}
      className="theme-toggle-button flex items-center justify-center rounded-lg transition-all duration-200 hover:scale-105 focus-ring"
      style={{
        width: '40px',
        height: '40px',
        backgroundColor: 'var(--bg-hover)',
        color: 'var(--header-icon-color)',
        border: 'none'
      }}
      title={`Switch to ${isDark ? 'light' : 'dark'} theme`}
    >
      <div className="theme-icon-container relative">
        {/* Sun Icon */}
        <Sun 
          size={18} 
          className={`theme-icon absolute transition-all duration-300 ${
            isDark ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
          }`}
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
        />
        
        {/* Moon Icon */}
        <Moon 
          size={18} 
          className={`theme-icon absolute transition-all duration-300 ${
            isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
          }`}
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
        />
      </div>
      
      {showLabel && (
        <span className="ml-2 text-sm font-medium">
          {isDark ? 'Dark' : 'Light'}
        </span>
      )}
    </button>
  );
}
